// /reports/categories — Category Sales (PAGES.md §3.49)

import { useState, useMemo } from 'react';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import { PieChart } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  useReportQuery,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
} from '@/features/reports';
import type { CategorySalesRow } from '@/features/reports';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';

type Row = CategorySalesRow & { id: string };

function makeColumns(currency: CurrencyCode): ColumnDef<Row>[] {
  return [
    { key: 'category', header: 'Category', render: (_v, row) => <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.category}</span> },
    { key: 'quantity', header: 'Qty', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.quantity.toLocaleString('en-IN')}</span> },
    { key: 'revenue', header: 'Revenue', numeric: true, render: (_v, row) => <span className="text-sm font-semibold tabular-nums">{formatPaise(row.revenue, currency)}</span> },
    { key: 'percentage', header: 'Share', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.percentage.toFixed(1)}%</span> },
  ];
}

export function CategorySalesReportPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const filter = useReportFilter(timeRange, locationId);
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const csvDecimals = Math.round(Math.log10(subunit));
  const columns = useMemo(() => makeColumns(currency), [currency]);

  const { data, isLoading, error } = useReportQuery({
    key: 'categories',
    filter,
    queryFn: (f) => reportsApi.getCategorySales(f),
  });

  const rows: Row[] = useMemo(
    () => (data?.rows ?? []).map((r) => ({ ...r, id: r.category_id })),
    [data],
  );

  const pieData = rows.map((r) => ({ label: r.category, value: r.revenue / subunit }));

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'csv') {
      downloadCsv(
        `category-sales-${filter.range.from}-to-${filter.range.to}.csv`,
        [
          { header: 'Category', get: (r: Row) => r.category },
          { header: 'Qty', get: (r: Row) => r.quantity },
          { header: `Revenue (${symbol})`, get: (r: Row) => (r.revenue / subunit).toFixed(csvDecimals) },
          { header: 'Share %', get: (r: Row) => r.percentage.toFixed(1) },
        ],
        rows,
      );
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(fmt, 'Orio360 Category Sales', `${rows.length} categories • Top: ${rows[0]?.category ?? '—'}`);
    }
  };

  return (
    <ReportPageShell
      title="Category Sales"
      subtitle="Revenue and volume split across every product category."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'Categories' }]}
      accent="violet-pink"
      headerActions={<ReportExportMenu onExport={handleExport} disabled={!rows.length} />}
      filterBar={<ReportFilterBar timeRange={timeRange} onTimeRangeChange={setTimeRange} locationId={locationId} onLocationChange={setLocationId} />}
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={0} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          {pieData.length > 0 ? (
            <div className="rounded-2xl bg-white dark:bg-neutral-800/80 dark:backdrop-blur-xl shadow-md p-5 mb-6 animate-slide-up">
              <PieChart data={pieData} title={`Revenue by Category (${symbol})`} donut />
            </div>
          ) : null}
          <ReportTable title="Category Breakdown" columns={columns} data={rows} emptyTitle="No category data for selected period" totalCount={rows.length} />
        </>
      )}
    </ReportPageShell>
  );
}
